import { useTranslation } from 'react-i18next'
import type { PublicOffer } from '../../types/offers'
import { LocaleLink } from '@/components/LocaleLink'
import { useLocaleNavigate } from '@/hooks/use-locale-navigate'
import { OfferImagePanel, TYPE_CLASSES } from './OfferImagePanel'
import { DropsPrice } from './DropsPrice'
import { MerchantHoverCard } from './MerchantHoverCard'

interface OfferCompactRowProps {
  offer: PublicOffer
  onClick?: () => void
  hideMerchantName?: boolean
  newTab?: boolean
}

// overflow-hidden intentionally on OfferImagePanel (via rounded-l-md on its own wrapper),
// NOT on this outer element — same reason as OfferCard: overflow-hidden + CSS transform
// on the same element clips fixed-position portals (Radix HoverCard).
const sharedClass =
  'grid grid-cols-[10%_1fr] items-center rounded-md border border-sand-300 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-500 hover:-translate-y-0.5 hover:shadow-md dark:hover:shadow-slate-900/50 transition-all duration-150 ease-out'

/**
 * Dense single-line row — 10% image / 90% content.
 * Image uses OfferImagePanel (CSS bg-image, no chip).
 * Chip rendered in the content area alongside price.
 */
export function OfferCompactRow({ offer, onClick, hideMerchantName, newTab }: OfferCompactRowProps) {
  const { buildPath } = useLocaleNavigate()
  const { t } = useTranslation('offers')

  const inner = (
    <>
      <OfferImagePanel
        offer={offer}
        className="h-full min-h-[48px] rounded-l-md"
        showChip={false}
        sizes="10vw"
      />
      <div className="flex items-center justify-between gap-3 px-3 py-2">
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-slate-800 dark:text-sand-200 truncate block">
            {offer.title}
          </span>
          {!hideMerchantName && (
            <MerchantHoverCard
              merchantBusinessName={offer.merchantBusinessName}
              slug={offer.merchantSlug}
              description={offer.merchantDescription}
              since={offer.merchantSince}
              avatarUrl={offer.merchantAvatarUrl}
            >
              {offer.merchantSlug ? (
                <LocaleLink
                  to={`/streams/${offer.merchantSlug}`}
                  onClick={(e) => e.stopPropagation()}
                  className="text-xs text-slate-500 dark:text-slate-400 truncate inline-block max-w-full hover:text-slate-800 dark:hover:text-white hover:underline transition-colors"
                >
                  {offer.merchantBusinessName}
                </LocaleLink>
              ) : (
                <span className="text-xs text-slate-500 truncate inline-block max-w-full">
                  {offer.merchantBusinessName}
                </span>
              )}
            </MerchantHoverCard>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${TYPE_CLASSES[offer.offerType]}`}
          >
            {t(`fields.offerType.${offer.offerType}`)}
          </span>
          <DropsPrice
            count={offer.priceDrops}
            iconSize="w-3 h-3"
            className="text-xs font-medium text-slate-700 dark:text-sand-200 whitespace-nowrap"
          />
        </div>
      </div>
    </>
  )

  if (onClick) {
    return (
      <button type="button" onClick={onClick} data-testid="offer-compact-row" className={`${sharedClass} w-full text-start cursor-pointer`}>
        {inner}
      </button>
    )
  }

  if (!offer.merchantSlug) {
    return <div data-testid="offer-compact-row" className={sharedClass}>{inner}</div>
  }

  const linkPath = `/streams/${offer.merchantSlug}/offers/${offer.id}`

  if (newTab) {
    return (
      <a
        href={buildPath(linkPath)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="offer-compact-row"
        className={sharedClass}
      >
        {inner}
      </a>
    )
  }

  return (
    <LocaleLink to={linkPath} data-testid="offer-compact-row" className={sharedClass}>
      {inner}
    </LocaleLink>
  )
}
